// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';

import type {
  FiltroDeMedicoes,
  MedicaoDetalhe,
  MedicaoResumo,
  MensagemDoChat,
  ResumoFinanceiro,
} from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados do Financeiro do Portal do Credenciado.
 *
 * Mesmo desenho das demais telas do módulo: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa, e o `queryFn` resolve
 * `dados.mock.json` em vez de chamar `chamarApi('/credenciado/...')` — ver a
 * justificativa em `comum/mock.ts`. Quando a API existir, cada `queryFn` troca
 * para `chamarApi(...)`, mantendo a mesma assinatura de hook.
 */
export const CHAVES_DO_FINANCEIRO = {
  resumo: ['credenciado', 'financeiro', 'resumo'] as const,
  medicoes: (filtro: FiltroDeMedicoes) =>
    ['credenciado', 'financeiro', 'medicoes', filtro] as const,
  medicao: (id: string | undefined) => ['credenciado', 'financeiro', 'medicao', id] as const,
};

const MEDICOES_MOCK = dadosMock.medicoes as MedicaoDetalhe[];

export function useResumoFinanceiro(): UseQueryResult<ResumoFinanceiro, Error> {
  return useQuery<ResumoFinanceiro, Error>({
    queryKey: CHAVES_DO_FINANCEIRO.resumo,
    queryFn: () => resolverMock(dadosMock.resumo as ResumoFinanceiro),
  });
}

/**
 * A listagem de medições, paginada no mesmo formato que a API real vai
 * devolver (`comum/contrato.ts`). O filtro por status acontece antes da
 * paginação — igual a um `WHERE` que roda antes do `LIMIT/OFFSET` no
 * servidor.
 */
export function useMedicoes(
  filtro: FiltroDeMedicoes,
): UseQueryResult<Pagina<MedicaoResumo>, Error> {
  return useQuery<Pagina<MedicaoResumo>, Error>({
    queryKey: CHAVES_DO_FINANCEIRO.medicoes(filtro),
    queryFn: () => {
      const filtradas = filtro.status
        ? MEDICOES_MOCK.filter((medicao) => medicao.status === filtro.status)
        : MEDICOES_MOCK;
      return resolverMock(paginar(filtradas, filtro.pagina, filtro.tamanho));
    },
  });
}

/**
 * Demonstrativo completo de uma medição. `undefined` para `id` deixa a busca
 * desligada (`enabled`); quando o `id` não bate com nenhuma medição, resolve
 * para `null` — a tela usa isso para mostrar `ErrorState` de recurso não
 * encontrado, distinto do estado de carregando.
 */
export function useMedicaoDetalhe(
  id: string | undefined,
): UseQueryResult<MedicaoDetalhe | null, Error> {
  return useQuery<MedicaoDetalhe | null, Error>({
    queryKey: CHAVES_DO_FINANCEIRO.medicao(id),
    queryFn: () => resolverMock(MEDICOES_MOCK.find((medicao) => medicao.id === id) ?? null),
    enabled: !!id,
  });
}

/**
 * Mensagens do chat com a SME sobre uma medição. Não há API de mensagens nem
 * conversa no mock: a consulta devolve a conversa vazia, que é a verdade — nada
 * foi registrado. A solicitação de revisão também não tem camada de dados:
 * `SolicitarRevisaoModal.tsx` só mostra o aviso de indisponibilidade.
 */
export function useMensagensDaMedicao(
  medicaoId: string | null,
): UseQueryResult<MensagemDoChat[], Error> {
  return useQuery<MensagemDoChat[], Error>({
    queryKey: ['credenciado', 'financeiro', 'chat', medicaoId] as const,
    queryFn: () => resolverMock<MensagemDoChat[]>([], 200),
    enabled: !!medicaoId,
  });
}

/*
 * NÃO EXISTE GANCHO DE ENVIO DE MENSAGEM, E ISSO É DELIBERADO.
 *
 * Até 2026-10-02 `useEnviarMensagemDoChat` guardava a mensagem num mapa em
 * memória e a conversa passava a mostrá-la como enviada à SME; nada chegava a
 * ninguém, e tudo sumia no recarregamento. É fingir gravar, o que o dono
 * proibiu em 2026-10-02 (D-E). Hoje "Enviar mensagem" mostra o aviso de
 * indisponibilidade (`ChatComSmeModal.tsx`). Quando a API de mensagens
 * existir, o gancho volta chamando `chamarApi` de verdade.
 */
